/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AlertColor, Box, IconButton, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { MRT_SortingState } from 'material-react-table';
import { isEmpty } from 'lodash';

import AllJobsApiService from '../../../Jobs/Services/JobManagementService';
import { formatDate, formatTimeTo12Hr, getStoredCustomerDetails, hasEventId } from '../../../../Shared/Utilities/utils';
import {
  allSchedulesEventIds,
  CurrencySymbol,
  filterConfigs,
  FiltersOfAllJobs,
  FormFieldType,
  JobStatuses,
  NumberOfRowsOptions,
  PROCESS_EVENTS,
  SnackbarSeverity,
  states,
  Types,
} from '../../../../Shared/Constants/App';

import { JobType } from '../../../../API';
import { AdhocShiftsPayload, JobManagementType } from '../AllJobs/AllJobs.Model';
import { DropdownOptions, IOpenPosition, ScheduleData, Source } from './AllSchedules.Model';

import DataGrid from '../../../../Shared/Components/Common/DataGrid/DataGrid';
import PageTitle from '../../../../Shared/Components/Common/PageTitle/PageTitle';
import { RootState } from '../../../../Shared/Store/store';
import { FiltersState } from '../../../../Shared/Store/features/filtersSlice';
import { Labels } from '../../../../Shared/Components/Common/GraphContainer/GraphContainer';
import CreateAdhocShifts from '../../../Jobs/Components/CreateAdhocShifts/CreateAdhocShifts';
import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import { NoDataFound } from '../../../../Shared/Components/Common/NoDataFound/NoDataFound';
import FilterDropDownOptions from './FilterDropDownOptions';
import Modal from '../../../Jobs/Utilities/Shared/Components/Modal';

import { Icons } from '../../../../Shared/Constants/Icons';
import theme from '../../../../Shared/Themes/theme';

import { StyledPagination } from '../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import { PaginationStyle, searchFieldStyles } from './AllJobs.Style';
import { SearchFieldContainer, SearchInput, CustomerContainer } from '../../../../Shared/Components/Layout/styles';
import { ActionsColumn, Linking } from '../../../../Shared/Components/Common/DataGrid/DataGrid.styles';
import WhiteTooltip from '../../../../Shared/Components/Common/CommonStyle/Tooltip.Style';
import { Options } from '../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { DropDown, SelectInput } from '../../../../Shared/Components/Common/GraphContainer/GraphContainer.styles';
import { IProcessEvents } from '../../../../routes/config';
import { getScheduleColumns } from './AllSchedulesColumns';
import ModifyPopUp from '../../../Jobs/Components/JobDetails/ModifyPopUp';

const AllSchedules = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jobs, setJobs] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterValues, setFilterValues] = useState<any[]>([]);
  const [selectedJobName, setSelectedJobName] = useState<string[]>([]);
  const [selectedJobNumber, setSelectedJobNumber] = useState<string[]>([]);
  const [selectedWorkTicketNumber, setSelectedWorkTicketNumber] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState({
    jobAddress: [] as string[],
    stateCode: [] as string[],
    city: [] as string[],
  });
  const [selectedVendor, setSelectedVendor] = useState<string[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string[]>([]);
  const [selectedVendorAdminName, setSelectedVendorAdminName] = useState<string[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState({
    name: [] as string[],
    startDate: [] as string[],
    endDate: [] as string[],
    startTime: [] as string[],
    endTime: [] as string[],
    mdScheduleType: [] as string[],
  });
  const [selectedJobStartDate, setSelectedJobStartDate] = useState<string[]>([]);
  const [selectedJobEndDate, setSelectedJobEndDate] = useState<string[]>([]);
  const [selectedVendorAdminEmail, setSelectedVendorAdminEmail] = useState<string[]>([]);
  const [selectedCreatedOn, setSelectedCreatedOn] = useState<string[]>([]);
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [newSort, setNewSort] = useState<boolean>();
  const [newSortOrder, setNewSortOrder] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isValueOpen, setValueOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showSelectAll, setShowSelectAll] = useState(false);
  const [resetSelectionSignal, setResetSelectionSignal] = useState(0);

  const [selectedDropDownValue, setSelectedDropDownValue] = useState<DropdownOptions | ''>('');
  const [isCreateAdhocShift, setIsCreateAdhocShift] = useState<boolean>(false);
  const [isCreateShiftLoading, setIsCreateShiftLoading] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>(SnackbarSeverity.success);

  const [isFilterPopUpOpen, setIsFilterPopUpOpen] = useState(false);
  const [filterName, setFilterName] = useState<string>('');

  const [isTARequiredPopupOpen, setIsTARequiredPopupOpen] = useState(false);
  const [enableTimeAndAttendance, setEnableTimeAndAttendance] = useState(false);
  const [TimeAndAttendanceData, setTimeAndAttendanceData] = useState<ScheduleData>();
  const [isUpdateTALoading, setIsUpdateTALoading] = useState(false);

  const { t } = useTranslation(['messages', 'jobManagement']);
  const { userId, customerId } = getStoredCustomerDetails() || {};

  const styles = searchFieldStyles(searchText);

  const processEvents = localStorage.getItem('jobManagementProcessEvents');
  const jobManagementEvents: IProcessEvents[] = processEvents ? JSON.parse(processEvents) : [];

  const hasAnyEvent = jobManagementEvents?.some((event) => allSchedulesEventIds?.includes(String(event?.eventId)));
  const canShowActionsForSchedules = hasEventId(jobManagementEvents, PROCESS_EVENTS.ViewDetails);

  const toggleValue = () => {
    setValueOpen(!isValueOpen);
  };

  const filterState = useSelector((state: RootState) => state.filters);
  const didRestoreFilters = useRef(false);
  const dataFetchedRef = useRef(false);

  const renderCell = useCallback((renderedCellValue: React.ReactNode): string | number => {
    // If renderedCellValue is a string or number, return it. Otherwise, return a fallback '-'
    if (typeof renderedCellValue === 'string' || typeof renderedCellValue === 'number') {
      return renderedCellValue ? renderedCellValue : '-';
    }
    if (renderedCellValue && Array.isArray(renderedCellValue)) {
      return renderedCellValue[0]?.finalQuote || renderedCellValue[0]?.finalQuote === 0
        ? `${CurrencySymbol.Dollar}${Number(renderedCellValue[0]?.finalQuote).toFixed(2)}`
        : '-';
    }
    return '-';
  }, []);

  const renderOpenPositions = (renderedCellValue: React.ReactNode | IOpenPosition): string => {
    if (typeof renderedCellValue !== Types.Object || renderedCellValue === null) {
      return '-';
    }

    if (renderedCellValue && typeof renderedCellValue === Types.Object) {
      const { isPositionOpen, totalPositionsOpen } = renderedCellValue as IOpenPosition;
      return isPositionOpen ? `${t('jobManagement:yes')} (${totalPositionsOpen ?? 0})` : t('jobManagement:no');
    }
    return '-';
  };

  const fetchData = useCallback(
    async (
      currentPage: number,
      limit: number,
      sortBy?: string,
      sortOrder?: boolean,
      searchText?: string,
      jobName?: string,
      jobNumber?: string,
      jobAddress?: string,
      stateCode?: string,
      city?: string,
      vendorName?: string,
      customerName?: string,
      scheduleName?: string,
      vendorAdminName?: string,
      scheduleStartDate?: string,
      scheduleEndDate?: string,
      jobStartDate?: string,
      jobEndDate?: string,
      scheduleStartTime?: string,
      scheduleEndTime?: string,
      vendorAdminEmail?: string,
      createdOn?: string,
      workTicketNumber?: string,
      mdScheduleType?: string,
      refreshCache?: boolean
    ) => {
      setIsLoading(true);
      try {
        const sortOrderString = sortOrder !== undefined ? (sortOrder ? 'desc' : 'asc') : undefined;

        const allSchedulesResponse = await AllJobsApiService.allSchedules(currentPage, limit, userId, {
          vendorId: customerId,
          sortBy,
          sortOrder: sortOrderString,
          searchText,
          jobName,
          jobNumber,
          jobAddress,
          stateCode,
          city,
          vendorName,
          customerName,
          scheduleName,
          vendorAdminName,
          scheduleStartDate,
          scheduleEndDate,
          jobStartDate,
          jobEndDate,
          scheduleStartTime,
          scheduleEndTime,
          vendorAdminEmail,
          createdOn,
          workTicketNumber,
          mdScheduleType,
          refreshCache,
        });

        if (allSchedulesResponse?.data?.data) {
          setJobs(allSchedulesResponse.data.data);
          setTotalCount(allSchedulesResponse?.data?.metadata?.totalCount);
        } else {
          console.error(t('messages:failedToFetchJobs'));
        }
        dataFetchedRef.current = true;
      } catch (error) {
        console.error(t('messages:errorFetchingJobs'), error);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const fetchFilterValues = useCallback(async () => {
    try {
      setIsFiltersLoading(true);
      const filterValuesResponse = await AllJobsApiService.getFilterValues(userId);
      const validData = filterValuesResponse?.data ? filterValuesResponse?.data : [];
      setFilterValues(validData);
    } catch (error) {
      console.error(t('messages:errorFetchingFilterValues'), error);
    }
    setIsFiltersLoading(false);
  }, []);

  const handleChange = (event: any, value: number) => {
    const {
      name: selectedScheduleName,
      startDate: selectedScheduleStartDate,
      endDate: selectedScheduleEndDate,
      startTime: selectedScheduleStartTime,
      endTime: selectedScheduleEndTime,
      mdScheduleType: selectedMdScheduleType,
    } = selectedSchedule;
    const { jobAddress: selectedJobAddress, stateCode: selectedState, city: selectedCity } = selectedLocation;

    setCurrentPage(value);
    fetchDataWithDebounce({
      currentPage: value,
      itemsPerPage,
      newSortOrder,
      newSort,
      searchText,
      selectedJobName,
      selectedJobNumber,
      selectedJobAddress,
      selectedState,
      selectedCity,
      selectedVendor,
      selectedCustomer,
      selectedScheduleName,
      selectedVendorAdminName,
      selectedScheduleStartDate,
      selectedScheduleEndDate,
      selectedJobStartDate,
      selectedJobEndDate,
      selectedScheduleStartTime,
      selectedScheduleEndTime,
      selectedVendorAdminEmail,
      selectedCreatedOn,
      selectedWorkTicketNumber,
      selectedMdScheduleType,
    });
  };
  const handleSort = (sortingObj: MRT_SortingState) => {
    setSorting(sortingObj);
    const {
      name: selectedScheduleName,
      startDate: selectedScheduleStartDate,
      endDate: selectedScheduleEndDate,
      startTime: selectedScheduleStartTime,
      endTime: selectedScheduleEndTime,
      mdScheduleType: selectedMdScheduleType,
    } = selectedSchedule;
    const { jobAddress: selectedJobAddress, stateCode: selectedState, city: selectedCity } = selectedLocation;

    if (sortingObj.length) {
      const { id, desc } = sortingObj[0];
      setNewSortOrder(id);
      setNewSort(desc);
      fetchDataWithDebounce({
        currentPage,
        itemsPerPage,
        newSortOrder: id,
        newSort: desc,
        searchText,
        selectedJobName,
        selectedJobNumber,
        selectedJobAddress,
        selectedState,
        selectedCity,
        selectedVendor,
        selectedCustomer,
        selectedScheduleName,
        selectedVendorAdminName,
        selectedScheduleStartDate,
        selectedScheduleEndDate,
        selectedJobStartDate,
        selectedJobEndDate,
        selectedScheduleStartTime,
        selectedScheduleEndTime,
        selectedVendorAdminEmail,
        selectedCreatedOn,
        selectedWorkTicketNumber,
        selectedMdScheduleType,
      });
    }
  };

  const renderActionsCell = (row: any): JSX.Element => {
    const { facilityId, facilityName, jobId, jobAddress, customerId, workOrderScheduleId } = row.original;
    const goToDetailsPath =
      row.original.jobStatus === JobStatuses.Completed ? `/onetimejobdetails?id=${jobId}` : `/job-detail/worktasks`;
    const goToDetailsState =
      row.original.jobStatus === JobStatuses.Completed
        ? {
            facilityItem: {
              ...row.original,
            },
            jobStatus: row.original.jobStatus,
            facility: {
              ...row.original.facilityName,
              address: row.original.jobAddress,
            },
            job: {
              ...row.original,
              customerId,
            },
          }
        : {
            facilityItem: {
              facilityId,
              facilityName,
              workOrderScheduleId,
              jobAddress,
            },
            jobData: jobs[row.index],
            customerId,
            jobId,
            isJobManagement: true,
          };

    return (
      <ActionsColumn>
        {hasEventId(jobManagementEvents, PROCESS_EVENTS.ViewDetails) && (
          <Linking to={goToDetailsPath} state={goToDetailsState} key={3}>
            <IconButton
              size="large"
              sx={{
                '&:hover': {
                  background: theme.palette.primary.main,
                },
              }}>
              <WhiteTooltip title={t('homePage:viewDetails')}>
                <img src={Icons.EyeIcon} alt={t('altTexts:viewDetails')} />
              </WhiteTooltip>
            </IconButton>
          </Linking>
        )}
      </ActionsColumn>
    );
  };

  const filters = Array.isArray(filterConfigs)
    ? // eslint-disable-next-line sonarjs/cognitive-complexity
      filterConfigs.map(({ title, name, key, format }) => {
        return {
          title,
          type: FormFieldType.DropDown,
          name,
          options: Array.from(
            new Map(
              Array.isArray(filterValues)
                ? filterValues
                    .filter((job) => {
                      const actualKey =
                        name === FiltersOfAllJobs.JobNumber
                          ? job.jobType === JobType.Recurring || job.jobType === JobType.Periodic
                            ? 'intId'
                            : 'intReferenceId'
                          : key;
                      return job?.[actualKey];
                    })
                    .map((job) => {
                      const actualKey =
                        name === FiltersOfAllJobs.JobNumber
                          ? job.jobType === JobType.Recurring || job.jobType === JobType.Periodic
                            ? 'intId'
                            : 'intReferenceId'
                          : key;
                      const value = job?.[actualKey];
                      let formattedLabel = job?.[actualKey];
                      if (format === 'date') {
                        formattedLabel = formatDate(job?.[actualKey]);
                      } else if (format === 'time') {
                        formattedLabel = formatTimeTo12Hr(job?.[actualKey]);
                      } else if (states[value as keyof typeof states]) {
                        formattedLabel = states[value as keyof typeof states];
                      }

                      return [value, { label: formattedLabel, value }];
                    })
                : []
            ).values() || []
          ),
        };
      })
    : [];

  const debounce = <T extends (...args: any[]) => void>(func: T, delay: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const fetchDataWithDebounce = debounce((params) => {
    fetchData(
      params.currentPage,
      params.itemsPerPage,
      params.newSortOrder,
      params.newSort,
      params.searchText,
      params.selectedJobName,
      params.selectedJobNumber,
      params.selectedJobAddress,
      params.selectedState,
      params.selectedCity,
      params.selectedVendor,
      params.selectedCustomer,
      params.selectedScheduleName,
      params.selectedVendorAdminName,
      params.selectedScheduleStartDate,
      params.selectedScheduleEndDate,
      params.selectedJobStartDate,
      params.selectedJobEndDate,
      params.selectedScheduleStartTime,
      params.selectedScheduleEndTime,
      params.selectedVendorAdminEmail,
      params.selectedCreatedOn,
      params.selectedWorkTicketNumber,
      params.selectedMdScheduleType,
      params.refreshCache
    );
  }, 500);

  const debouncedSearch = useCallback(
    debounce((text: string) => {
      const {
        name: selectedScheduleName,
        startDate: selectedScheduleStartDate,
        endDate: selectedScheduleEndDate,
        startTime: selectedScheduleStartTime,
        endTime: selectedScheduleEndTime,
        mdScheduleType: selectedMdScheduleType,
      } = selectedSchedule;
      const { jobAddress: selectedJobAddress, stateCode: selectedState, city: selectedCity } = selectedLocation;
      fetchDataWithDebounce({
        currentPage,
        itemsPerPage,
        newSortOrder,
        newSort,
        searchText: text,
        selectedJobName,
        selectedJobNumber,
        selectedJobAddress,
        selectedState,
        selectedCity,
        selectedVendor,
        selectedCustomer,
        selectedScheduleName,
        selectedVendorAdminName,
        selectedScheduleStartDate,
        selectedScheduleEndDate,
        selectedJobStartDate,
        selectedJobEndDate,
        selectedScheduleStartTime,
        selectedScheduleEndTime,
        selectedVendorAdminEmail,
        selectedCreatedOn,
        selectedWorkTicketNumber,
        selectedMdScheduleType,
      });
    }, 1500),
    [
      currentPage,
      itemsPerPage,
      newSortOrder,
      newSort,
      selectedJobName,
      selectedJobNumber,
      selectedLocation,
      selectedVendor,
      selectedCustomer,
      selectedSchedule,
      selectedVendorAdminName,
      selectedJobStartDate,
      selectedJobEndDate,
      selectedVendorAdminEmail,
      selectedCreatedOn,
      selectedWorkTicketNumber,
    ]
  );

  const handleSearchTextChange = (text: string) => {
    setResetSelectionSignal((prev) => prev + 1);

    setSearchText(text);
    debouncedSearch(text);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleFilterChange = (filterType: string, filterValue: string[]) => {
    const value = filterValue;
    switch (filterType) {
      case 'jobName':
        setSelectedJobName(filterValue);
        break;
      case 'jobAddress':
        setSelectedLocation((prev) => ({ ...prev, jobAddress: value }));
        break;
      case 'stateCode':
        setSelectedLocation((prev) => ({ ...prev, stateCode: value }));
        break;
      case 'city':
        setSelectedLocation((prev) => ({ ...prev, city: value }));
        break;
      case 'vendor':
        setSelectedVendor(filterValue);
        break;
      case 'customer':
        setSelectedCustomer(filterValue);
        break;
      case 'jobNumber':
        setSelectedJobNumber(filterValue);
        break;
      case 'vendorAdminName':
        setSelectedVendorAdminName(filterValue);
        break;
      case 'scheduleStartDate':
        setSelectedSchedule((prev) => ({ ...prev, startDate: value }));
        break;
      case 'scheduleEndDate':
        setSelectedSchedule((prev) => ({ ...prev, endDate: value }));
        break;
      case 'jobStartDate':
        setSelectedJobStartDate(filterValue);
        break;
      case 'jobEndDate':
        setSelectedJobEndDate(filterValue);
        break;
      case 'scheduleStartTime':
        setSelectedSchedule((prev) => ({ ...prev, startTime: value }));
        break;
      case 'scheduleEndTime':
        setSelectedSchedule((prev) => ({ ...prev, endTime: value }));
        break;
      case 'vendorAdminEmail':
        setSelectedVendorAdminEmail(filterValue);
        break;
      case 'createdOn':
        setSelectedCreatedOn(filterValue);
        break;
      case 'workTicketNumber':
        setSelectedWorkTicketNumber(filterValue);
        break;
      case 'mdScheduleType':
        setSelectedSchedule((prev) => ({ ...prev, mdScheduleType: value }));
        break;
      default:
        break;
    }

    fetchDataWithDebounce({
      currentPage,
      itemsPerPage,
      newSortOrder,
      newSort,
      searchText,
      selectedJobName: filterType === 'jobName' ? filterValue : selectedJobName,
      selectedJobNumber: filterType === 'jobNumber' ? filterValue : selectedJobNumber,
      selectedJobAddress: filterType === 'jobAddress' ? filterValue : selectedLocation.jobAddress,
      selectedState: filterType === 'stateCode' ? filterValue : selectedLocation.stateCode,
      selectedCity: filterType === 'city' ? filterValue : selectedLocation.city,
      selectedVendor: filterType === 'vendor' ? filterValue : selectedVendor,
      selectedCustomer: filterType === 'customer' ? filterValue : selectedCustomer,
      selectedVendorAdminName: filterType === 'vendorAdminName' ? filterValue : selectedVendorAdminName,
      selectedScheduleStartDate: filterType === 'scheduleStartDate' ? filterValue : selectedSchedule.startDate,
      selectedScheduleEndDate: filterType === 'scheduleEndDate' ? filterValue : selectedSchedule.endDate,
      selectedJobStartDate: filterType === 'jobStartDate' ? filterValue : selectedJobStartDate,
      selectedJobEndDate: filterType === 'jobEndDate' ? filterValue : selectedJobEndDate,
      selectedScheduleStartTime: filterType === 'scheduleStartTime' ? filterValue : selectedSchedule.startTime,
      selectedScheduleEndTime: filterType === 'scheduleEndTime' ? filterValue : selectedSchedule.endTime,
      selectedVendorAdminEmail: filterType === 'vendorAdminEmail' ? filterValue : selectedVendorAdminEmail,
      selectedCreatedOn: filterType === 'createdOn' ? filterValue : selectedCreatedOn,
      selectedWorkTicketNumber: filterType === 'workTicketNumber' ? filterValue : selectedWorkTicketNumber,
      selectedMdScheduleType: filterType === 'mdScheduleType' ? filterValue : selectedSchedule.mdScheduleType,
    });
    handleCloseFilterPopUp();
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newItemsPerPage = Number(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    const {
      name: selectedScheduleName,
      startDate: selectedScheduleStartDate,
      endDate: selectedScheduleEndDate,
      startTime: selectedScheduleStartTime,
      endTime: selectedScheduleEndTime,
      mdScheduleType: selectedMdScheduleType,
    } = selectedSchedule;
    const { jobAddress: selectedJobAddress, stateCode: selectedState, city: selectedCity } = selectedLocation;

    fetchDataWithDebounce({
      currentPage: 1,
      itemsPerPage: newItemsPerPage,
      newSortOrder,
      newSort,
      searchText,
      selectedJobName,
      selectedJobNumber,
      selectedJobAddress,
      selectedState,
      selectedCity,
      selectedVendor,
      selectedCustomer,
      selectedScheduleName,
      selectedVendorAdminName,
      selectedScheduleStartDate,
      selectedScheduleEndDate,
      selectedJobStartDate,
      selectedJobEndDate,
      selectedScheduleStartTime,
      selectedScheduleEndTime,
      selectedVendorAdminEmail,
      selectedCreatedOn,
      selectedWorkTicketNumber,
      selectedMdScheduleType,
    });
  };

  const columns = useMemo(
    () =>
      getScheduleColumns(
        renderCell,
        renderOpenPositions,
        renderActionsCell,
        t,
        filters,
        handleFilterChange,
        isFiltersLoading,
        selectedJobNumber,
        selectedJobName,
        selectedLocation.jobAddress,
        selectedLocation.stateCode,
        selectedLocation.city,
        selectedVendor,
        selectedCustomer,
        selectedSchedule.name,
        selectedVendorAdminName,
        selectedSchedule.startDate,
        selectedSchedule.endDate,
        selectedJobStartDate,
        selectedJobEndDate,
        selectedSchedule.startTime,
        selectedSchedule.endTime,
        selectedVendorAdminEmail,
        selectedCreatedOn,
        selectedWorkTicketNumber,
        setIsFilterPopUpOpen,
        setFilterName,
        isLoading,
        setIsTARequiredPopupOpen,
        setEnableTimeAndAttendance,
        setTimeAndAttendanceData
      ),
    [jobs, renderCell, renderActionsCell, fetchData]
  );

  const restoreFilters = (filters: FiltersState) => {
    setSelectedJobName(filters?.jobName || '');
    setSelectedLocation({
      jobAddress: filters?.jobAddress || '',
      stateCode: [],
      city: [],
    });
    setSelectedCustomer(filters?.customer || '');
    setSelectedJobNumber(filters?.jobNumber || '');

    fetchDataWithDebounce({
      currentPage,
      itemsPerPage,
      newSortOrder,
      newSort,
      searchText,
      selectedJobName: filters?.jobName,
      selectedJobNumber: filters?.jobNumber,
      selectedJobAddress: filters?.jobAddress,
      selectedCustomer: filters?.customer,
    });
  };

  const handleSelectAllData = async () => {
    try {
      const {
        name: selectedScheduleName,
        startDate: selectedScheduleStartDate,
        endDate: selectedScheduleEndDate,
        startTime: selectedScheduleStartTime,
        endTime: selectedScheduleEndTime,
        mdScheduleType: selectedMdScheduleType,
      } = selectedSchedule;
      const { jobAddress: selectedJobAddress, stateCode: selectedState, city: selectedCity } = selectedLocation;

      const allSchedulesResponse = await AllJobsApiService.allSchedules(currentPage, totalCount, userId, {
        vendorId: customerId,
        newSortOrder,
        sortOrder: newSort,
        searchText,
        jobName: selectedJobName,
        jobNumber: selectedJobNumber,
        jobAddress: selectedJobAddress,
        selectedState,
        selectedCity,
        selectedVendor,
        customerName: selectedCustomer,
        selectedScheduleName,
        selectedVendorAdminName,
        selectedScheduleStartDate,
        selectedScheduleEndDate,
        selectedJobStartDate,
        selectedJobEndDate,
        selectedScheduleStartTime,
        selectedScheduleEndTime,
        selectedVendorAdminEmail,
        selectedCreatedOn,
        selectedWorkTicketNumber,
        selectedMdScheduleType,
      });

      if (allSchedulesResponse?.data?.data) {
        return allSchedulesResponse?.data?.data;
      } else {
        console.error(t('messages:failedToFetchJobs'));
        return [];
      }
    } catch (error) {
      console.error(t('jobManagement:failedToSelectAllRows'), error);
      return [];
    }
  };

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  const createAdhocShifts = useCallback(async (details: AdhocShiftsPayload) => {
    try {
      setIsCreateShiftLoading(true);
      details.source = Source.Peazy;
      const { userId } = getStoredCustomerDetails() || {};

      const date = new Date(details?.scheduleStartDate ?? '');

      date?.setDate(date?.getDate() + Number(details?.daysExtendedBy));
      const scheduleEndDate = date?.toISOString()?.split('T')[0];
      details.scheduleEndDate = scheduleEndDate;

      const response = await AllJobsApiService.createAdhocShifts(details, userId);
      if (!isEmpty(response?.data) && isEmpty(response?.errors)) {
        showSnackbar(SnackbarSeverity.success, t('jobManagement:unScheduledWorkSuccessMessage'));
      } else {
        showSnackbar(SnackbarSeverity.error, t('jobManagement:unScheduledWorkErrorMessage'));
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsCreateShiftLoading(false);
      setIsCreateAdhocShift(false);
      setSelectedDropDownValue('');
    }
  }, []);

  const closeAdhocShift = () => {
    setIsCreateAdhocShift(false);
    setSelectedDropDownValue('');
  };

  const fetchAllSchedules = (refreshCache?: boolean) => {
    const {
      name: selectedScheduleName,
      startDate: selectedScheduleStartDate,
      endDate: selectedScheduleEndDate,
      startTime: selectedScheduleStartTime,
      endTime: selectedScheduleEndTime,
      mdScheduleType: selectedMdScheduleType,
    } = selectedSchedule;
    fetchDataWithDebounce({
      currentPage,
      itemsPerPage,
      newSortOrder,
      newSort,
      searchText,
      selectedJobName,
      selectedJobNumber,
      selectedLocation,
      selectedVendor,
      selectedCustomer,
      selectedScheduleName,
      selectedVendorAdminName,
      selectedScheduleStartDate,
      selectedScheduleEndDate,
      selectedJobStartDate,
      selectedJobEndDate,
      selectedScheduleStartTime,
      selectedScheduleEndTime,
      selectedVendorAdminEmail,
      selectedCreatedOn,
      selectedMdScheduleType,
      refreshCache,
    });
  };

  useEffect(() => {
    const hasAnyFilter =
      !isEmpty(searchText) ||
      !isEmpty(selectedJobName) ||
      !isEmpty(selectedJobNumber) ||
      !isEmpty(selectedLocation?.jobAddress) ||
      !isEmpty(selectedCustomer);
    if (hasAnyFilter) {
      setShowSelectAll(true);
    } else {
      setShowSelectAll(false);
    }
  }, [searchText, selectedJobName, selectedJobNumber, selectedLocation, selectedCustomer]);

  useEffect(() => {
    const hasFilter =
      filterState?.jobName || filterState?.jobNumber || filterState?.jobAddress || filterState?.customer;
    if (hasFilter) {
      didRestoreFilters.current = true;
      restoreFilters(filterState);
    }
  }, []);

  useEffect(() => {
    if (didRestoreFilters.current) {
      didRestoreFilters.current = false;
      return;
    }
    fetchAllSchedules();
  }, []);

  useEffect(() => {
    if (!isEmpty(jobs)) {
      fetchFilterValues();
    }
  }, [jobs]);

  useEffect(() => {
    localStorage.removeItem('woScheduleMasterIds');
    localStorage.removeItem('workOrderIds');
  }, []);

  useEffect(() => {
    if (selectedDropDownValue === DropdownOptions.CreateAdhocShifts) {
      setIsCreateAdhocShift(true);
    }
  }, [selectedDropDownValue]);

  const handleCloseFilterPopUp = () => {
    setIsFilterPopUpOpen(false);
  };

  const fetchSelectedFilter = () => {
    const { jobAddress: selectedJobAddress } = selectedLocation;

    switch (filterName) {
      case FiltersOfAllJobs.JobName:
        return selectedJobName;
        break;
      case FiltersOfAllJobs.JobAddress:
        return selectedJobAddress;
        break;
      case FiltersOfAllJobs.Customer:
        return selectedCustomer;
        break;
      case FiltersOfAllJobs.JobNumber:
        return selectedJobNumber;
        break;
      default:
        break;
    }
  };

  const renderFilterBodyContent = () => {
    return (
      <>
        <FilterDropDownOptions
          options={filters?.find((filter) => filter?.name === filterName)?.options || []}
          handleFilterChange={(value) => handleFilterChange(filterName, value)}
          filterLoader={isFiltersLoading}
          selectedFilter={fetchSelectedFilter()}
        />
      </>
    );
  };

  const handleUpdateRouting = async () => {
    try {
      setIsUpdateTALoading(true);
      const woScheduleMasterId = TimeAndAttendanceData?.woScheduleMasterId;
      const timeAndAttendanceRequired = enableTimeAndAttendance ? true : false;
      if (woScheduleMasterId) {
        const result = await AllJobsApiService.updateRouting(woScheduleMasterId, timeAndAttendanceRequired, userId);
        if (!isEmpty(result?.data) && isEmpty(result?.errors)) {
          showSnackbar(SnackbarSeverity.success, t('jobManagement:routingSuccessMessage'));
        } else if (!isEmpty(result?.errors) && isEmpty(result?.data)) {
          showSnackbar(SnackbarSeverity.error, t('jobManagement:routingErrorMessage'));
        }
      }
      setIsUpdateTALoading(false);
      setIsTARequiredPopupOpen(false);
      fetchAllSchedules();
    } catch (e) {
      console.error('Error occurred while updating Routing', e);
    }
  };

  return (
    <>
      <PageTitle title={t('jobManagement:allSchedules')} />
      <CustomerContainer>
        {dataFetchedRef.current && !isLoading && !Array.isArray(jobs) ? (
          <NoDataFound />
        ) : (
          <>
            <SearchFieldContainer sx={{ ...styles }}>
              <SearchInput
                placeholder={t('oneTimeJob:search')}
                value={searchText}
                onChange={(e) => handleSearchTextChange(e.target.value)}
              />
              <img src={Icons.SearchIcon} alt={t('altTexts:search')} />
            </SearchFieldContainer>
            <DataGrid
              columns={columns}
              data={jobs}
              enableRowSelect={true}
              enableColumnPinning={true}
              loader={isLoading}
              errorMessageTitle={t('noData:noDataYet')}
              errorMessageDescription={t('noData:comeBackLater')}
              configureColumns={true}
              sortingObj={sorting}
              sort={handleSort}
              selectActions={hasAnyEvent}
              setSelectedRows={setSelectedRows}
              selectedRows={selectedRows}
              type={JobManagementType.AllSchedules}
              setSelectedDropDownValue={setSelectedDropDownValue}
              selectedDropDownValue={selectedDropDownValue}
              totalCount={totalCount}
              onFetchAllRows={handleSelectAllData}
              showSelectAllButton={showSelectAll}
              resetSelectionSignal={resetSelectionSignal}
              filters={{
                jobName: selectedJobName,
                jobNumber: selectedJobNumber,
                jobAddress: selectedLocation?.jobAddress,
                customer: selectedCustomer,
              }}
              storeRowSelection={true}
              fetchData={() => fetchAllSchedules(true)}
              processEvents={jobManagementEvents}
              canShowActionsForSchedules={canShowActionsForSchedules}
              columnFilterDisplayMode={'custom'}
            />
          </>
        )}
        {!isLoading && totalCount > 0 && (
          <PaginationStyle>
            <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
              <StyledPagination
                count={Math.ceil(totalCount / itemsPerPage)}
                page={currentPage}
                onChange={(event, value) => {
                  handleChange(event, value);
                  if (newSortOrder) {
                    fetchData(value, itemsPerPage, newSortOrder, newSort);
                  } else {
                    fetchData(value, itemsPerPage);
                  }
                }}
                color="primary"
              />
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3rem' }}>
              <Typography>
                {t('dashboard:totalItems')}: {totalCount}
              </Typography>
              {totalCount > 10 && (
                <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <Typography>{t('dashboard:recordsPerPage')}:</Typography>
                  <DropDown>
                    <Box onClick={toggleValue}>
                      <Select
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        label="Records"
                        input={<SelectInput />}
                        IconComponent={() => (
                          <img
                            src={Icons.DropdownIcon}
                            style={{
                              transform: isValueOpen ? Labels.Rotate : 'none',
                              position: 'absolute',
                              right: '0.75rem',
                              cursor: 'pointer',
                            }}
                          />
                        )}
                        open={isValueOpen}
                        onClose={toggleValue}
                        onOpen={toggleValue}>
                        {NumberOfRowsOptions.map((dataItem) => (
                          <Options key={dataItem.id} value={dataItem.value}>
                            {dataItem.label}
                          </Options>
                        ))}
                      </Select>
                    </Box>
                  </DropDown>
                </Box>
              )}
            </Box>
          </PaginationStyle>
        )}
        <CreateAdhocShifts
          openShiftCard={isCreateAdhocShift}
          createAdhocShifts={createAdhocShifts}
          closeShiftCard={() => closeAdhocShift()}
          isLoading={isCreateShiftLoading}
          selectedRows={selectedRows}
        />
        <SnackbarMessage
          open={snackbarOpen}
          successMessage={snackbarMessage}
          errorMessage={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
        <Modal
          open={isFilterPopUpOpen}
          onClose={handleCloseFilterPopUp}
          renderHeader={''}
          renderBody={renderFilterBodyContent()}
          styles={{
            '& .MuiPaper-root': { width: '32rem', padding: '1.5em' },
          }}
          isRemoveDivider={true}
        />
        <ModifyPopUp
          open={isTARequiredPopupOpen}
          handleClose={() => setIsTARequiredPopupOpen(false)}
          handleOnCloseJob={() => handleUpdateRouting()}
          loading={isUpdateTALoading}
          heading={''}
          text={
            <Trans
              i18nKey={enableTimeAndAttendance ? 'jobManagement:enableRouting' : 'jobManagement:disableRouting'}
              values={{
                jobNumber: TimeAndAttendanceData?.jobNumber,
                scheduleName: TimeAndAttendanceData?.scheduleName,
              }}
              components={{ bold: <strong /> }}
            />
          }
        />
      </CustomerContainer>
    </>
  );
};

export default AllSchedules;
